// The Test tools of the mock API: a small button in the bottom corner, on the sign-in pages and in the portal.
// It is there only while the mock API fills the screens (api.demo); with the real backend it disappears and no
// screen changes. Inside: the test users (password + current code, sign in as), the test clock, Meta's answer for
// waiting templates, and "Reset test data".
import { useState } from 'react'
import { FlaskConical } from 'lucide-react'
import { ConfirmDialog, Drawer, notify } from '@the-kit/ui'
import { Section } from '@/components/Section'
import { useIsDemo, useResetDemo } from '../useDemoControls'
import { PendingTemplates } from './PendingTemplates'
import { TestClock } from './TestClock'
import { TestUsers } from './TestUsers'

interface TestToolsButtonProps {
  /** the clock and the templates need someone signed in */
  signedIn?: boolean
  canAnswerMeta?: boolean
}

export function TestToolsButton({ signedIn = false, canAnswerMeta = false }: TestToolsButtonProps) {
  const isDemo = useIsDemo()
  const reset = useResetDemo()
  const [open, setOpen] = useState(false)
  const [asking, setAsking] = useState(false)
  if (!isDemo) return null
  return (
    <>
      <button
        type="button"
        className="btn btn-secondary btn-sm fixed bottom-3 right-3 z-40 shadow-card"
        onClick={() => setOpen(true)}
        data-testid="test-tools-open"
      >
        <FlaskConical size={13} /> Test tools
      </button>
      <Drawer
        isOpen={open}
        onClose={() => setOpen(false)}
        title="Test tools"
        subtitle="Mock API only: these go away when the real backend is connected."
        size="md"
      >
        <div data-testid="test-tools">
          <Section title="Test users">
            <TestUsers onSignedIn={() => setOpen(false)} />
          </Section>
          {signedIn && (
            <Section title="Clock">
              <TestClock />
            </Section>
          )}
          {signedIn && canAnswerMeta && (
            <Section title="Templates waiting for Meta">
              <PendingTemplates />
            </Section>
          )}
          <Section title="Test data">
            <p className="help mb-2">
              Back to the start: Saturday 2026-10-24 10:00, nobody signed in, every change gone.
            </p>
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={() => setAsking(true)}
              data-testid="reset-test-data"
            >
              Reset test data
            </button>
          </Section>
        </div>
      </Drawer>
      <ConfirmDialog
        isOpen={asking}
        type="warning"
        title="Reset the test data?"
        message="Every change goes and the clock is back at Saturday 2026-10-24 10:00. Nobody stays signed in."
        confirmText="Reset test data"
        onClose={() => setAsking(false)}
        onConfirm={() => {
          setAsking(false)
          setOpen(false)
          reset.mutate(undefined, { onSuccess: () => notify.info('Test data reset') })
        }}
      />
    </>
  )
}
